import React, { useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import '../styles/invite.css';

interface InviteUserFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function InviteUserForm({ onSuccess, onCancel }: InviteUserFormProps) {
  const { token } = useAuth();
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'admin' | 'employee'>('employee');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Bulk state
  type BulkRow = { email: string; firstName?: string; lastName?: string; role: 'admin' | 'employee' };
  type BulkResult = { row: number; email: string; status: 'invited' | 'skipped' | 'failed'; message?: string; userId?: string };
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([]);
  const [bulkResults, setBulkResults] = useState<BulkResult[] | null>(null);

  const bulkSummary = useMemo(() => {
    if (!bulkResults) return null;
    return {
      invited: bulkResults.filter((r) => r.status === 'invited').length,
      skipped: bulkResults.filter((r) => r.status === 'skipped').length,
      failed: bulkResults.filter((r) => r.status === 'failed').length,
      total: bulkResults.length,
    };
  }, [bulkResults]);

  const parseCsv = (text: string): BulkRow[] => {
    // Simple CSV parser (supports quoted values). Expected columns:
    // email, firstName, lastName, role
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length === 0) return [];

    const parseLine = (line: string): string[] => {
      const out: string[] = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          // Handle escaped quotes ""
          const next = line[i + 1];
          if (inQuotes && next === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          out.push(cur.trim());
          cur = '';
        } else {
          cur += ch;
        }
      }
      out.push(cur.trim());
      return out.map((v) => v.replace(/^"|"$/g, '').trim());
    };

    const headerCells = parseLine(lines[0]).map((h) => h.toLowerCase());
    const hasHeader = headerCells.includes('email');

    const startIdx = hasHeader ? 1 : 0;
    const rows: BulkRow[] = [];

    for (let i = startIdx; i < lines.length; i++) {
      const cells = parseLine(lines[i]);
      const email = (cells[0] || '').trim().toLowerCase();
      const firstName = (cells[1] || '').trim();
      const lastName = (cells[2] || '').trim();
      const roleRaw = (cells[3] || 'employee').trim().toLowerCase();
      const parsedRole = roleRaw === 'admin' ? 'admin' : 'employee';

      if (!email) continue;

      rows.push({
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        role: parsedRole,
      });
    }

    return rows;
  };

  const handleCsvUpload = async (file: File) => {
    setError('');
    setBulkResults(null);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        setError('No valid rows found. CSV must have columns: email, first name, last name, role');
        setBulkRows([]);
        return;
      }
      setBulkRows(rows);
    } catch (e: any) {
      setError(e?.message || 'Failed to read CSV');
      setBulkRows([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response =
        mode === 'single'
          ? await fetch(`${authConfig.apiUrl}/api/admin/invite-user`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                email: email.toLowerCase().trim(),
                firstName,
                lastName,
                role,
              }),
            })
          : await fetch(`${authConfig.apiUrl}/api/admin/invite-users/bulk`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                invites: bulkRows,
              }),
            });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to invite user');
      }

      if (mode === 'bulk') {
        setBulkResults(data.results || []);
      } else {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="invite-modal-overlay">
      <div className="invite-modal">
        <h2 className="invite-modal-title">Invite Users</h2>

        <div className="invite-tabs">
          <button
            type="button"
            className={`invite-tab ${mode === 'single' ? 'active' : ''}`}
            onClick={() => {
              setMode('single');
              setError('');
            }}
          >
            Single
          </button>
          <button
            type="button"
            className={`invite-tab ${mode === 'bulk' ? 'active' : ''}`}
            onClick={() => {
              setMode('bulk');
              setError('');
              setSuccess(false);
            }}
          >
            Bulk CSV
          </button>
        </div>

        <form onSubmit={handleSubmit} className="invite-form">
          {mode === 'single' ? (
            <>
              <div className="invite-input-group">
                <label className="invite-label">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="invite-input"
                  required
                  placeholder="user@example.com"
                />
              </div>

              <div className="invite-form-row">
                <div className="invite-input-group">
                  <label className="invite-label">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="invite-input"
                    placeholder="John"
                  />
                </div>

                <div className="invite-input-group">
                  <label className="invite-label">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="invite-input"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="invite-input-group">
                <label className="invite-label">Role *</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'admin' | 'employee')}
                  className="invite-select crewcast-select"
                  required
                >
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="invite-hint">User will be assigned to your company automatically</p>
              </div>
            </>
          ) : (
            <>
              <div className="invite-input-group">
                <label className="invite-label">Upload CSV *</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="invite-input"
                  required={bulkRows.length === 0}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleCsvUpload(file);
                  }}
                />
                <p className="invite-hint">
                  Columns: <code>email, firstName, lastName, role</code> (role: admin|employee)
                </p>
              </div>

              {bulkRows.length > 0 && (
                <div className="invite-bulk-preview">
                  <div className="invite-bulk-preview-header">
                    Parsed {bulkRows.length} row(s)
                  </div>
                  <div className="invite-bulk-preview-table">
                    <div className="invite-bulk-preview-row invite-bulk-preview-head">
                      <div>Email</div>
                      <div>First</div>
                      <div>Last</div>
                      <div>Role</div>
                    </div>
                    {bulkRows.slice(0, 10).map((r, idx) => (
                      <div key={idx} className="invite-bulk-preview-row">
                        <div>{r.email}</div>
                        <div>{r.firstName || ''}</div>
                        <div>{r.lastName || ''}</div>
                        <div>{r.role}</div>
                      </div>
                    ))}
                    {bulkRows.length > 10 && (
                      <div className="invite-bulk-preview-more">
                        …and {bulkRows.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {bulkResults && bulkSummary && (
                <div className="invite-bulk-results">
                  <div className="invite-success" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircle2 size={16} /> Bulk invite complete — Invited: {bulkSummary.invited}, Skipped: {bulkSummary.skipped}, Failed: {bulkSummary.failed}
                  </div>
                  <div className="invite-bulk-preview-table" style={{ marginTop: 10 }}>
                    <div className="invite-bulk-preview-row invite-bulk-preview-head">
                      <div>Email</div>
                      <div>Status</div>
                      <div>Message</div>
                    </div>
                    {bulkResults.slice(0, 20).map((r) => (
                      <div key={`${r.row}-${r.email}`} className="invite-bulk-preview-row">
                        <div>{r.email}</div>
                        <div>{r.status}</div>
                        <div>{r.message || ''}</div>
                      </div>
                    ))}
                    {bulkResults.length > 20 && (
                      <div className="invite-bulk-preview-more">
                        …and {bulkResults.length - 20} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {error && <div className="invite-error">{error}</div>}

          {success && (
            <div className="invite-success" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={16} /> User invited successfully! Invitation email sent.
            </div>
          )}

          <div className="invite-actions">
            <button
              type="button"
              onClick={onCancel}
              className="invite-cancel-button"
              disabled={loading || success}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="invite-submit-button"
              disabled={loading || success || (mode === 'bulk' && bulkRows.length === 0)}
            >
              {mode === 'bulk'
                ? loading
                  ? 'Sending Invites...'
                  : 'Send Invites'
                : loading
                ? 'Sending Invitation...'
                : success
                ? 'Invited!'
                : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
